import type { DailyRecordNoteRow, DailyRecordNotesByDate, DailyWeekNoteDraft } from '../types/dailyRecordNote';
import { weekNotesSignature } from './dailyRecordNotesWeekState';
import { clearDailyNoteCategoriesCache } from './dailyNoteCategoriesCache';
import { deviceCacheStorage } from './deviceCacheStorage';

const WEEK_PREFIX = 'cp.dailyRecordNotes.v2';
const DRAFT_PREFIX = 'cp.dailyRecordNotes.draft.v2';
const WEEK_ROWS_PREFIX = 'cp.dailyRecordNotes.weekRows.v1';
const DRAFT_ROWS_PREFIX = 'cp.dailyRecordNotes.draftRows.v1';
const LIST_PREFIX = 'cp.dailyRecordNotes.list.v3';

/** 1페이지: 생성 버튼 + 노트 9건. 2페이지부터: 노트 10건. */
export const DAILY_NOTES_PAGE1_SIZE = 9;
export const DAILY_NOTES_OTHER_PAGE_SIZE = 10;
/** @deprecated 호환용 — 1페이지 노트 수 */
export const DAILY_NOTES_PAGE_SIZE = DAILY_NOTES_PAGE1_SIZE;

export function dailyNotesPageSize(page: number): number {
  return page <= 1 ? DAILY_NOTES_PAGE1_SIZE : DAILY_NOTES_OTHER_PAGE_SIZE;
}

export function dailyNotesPageRange(page: number): { from: number; to: number; pageSize: number } {
  const safePage = Math.max(1, Math.floor(page) || 1);
  if (safePage <= 1) {
    return { from: 0, to: DAILY_NOTES_PAGE1_SIZE - 1, pageSize: DAILY_NOTES_PAGE1_SIZE };
  }
  const from = DAILY_NOTES_PAGE1_SIZE + (safePage - 2) * DAILY_NOTES_OTHER_PAGE_SIZE;
  const pageSize = DAILY_NOTES_OTHER_PAGE_SIZE;
  return { from, to: from + pageSize - 1, pageSize };
}

export function dailyNotesTotalPages(totalCount: number): number {
  if (totalCount <= DAILY_NOTES_PAGE1_SIZE) return 1;
  return 1 + Math.ceil((totalCount - DAILY_NOTES_PAGE1_SIZE) / DAILY_NOTES_OTHER_PAGE_SIZE);
}

function weekRowsCacheKey(userId: string, mondayIso: string): string {
  return `${WEEK_ROWS_PREFIX}.${userId}.${mondayIso}`;
}

function weekRowsDraftKey(userId: string, mondayIso: string): string {
  return `${DRAFT_ROWS_PREFIX}.${userId}.${mondayIso}`;
}

function isWeekNoteDraft(v: unknown): v is DailyWeekNoteDraft {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  return (
    typeof r.id === 'string' &&
    typeof r.note_date === 'string' &&
    typeof r.body === 'string' &&
    (r.category_id === null || typeof r.category_id === 'string') &&
    typeof r.persisted === 'boolean'
  );
}

function mapWeekDraft(n: DailyWeekNoteDraft): DailyWeekNoteDraft {
  return {
    id: n.id,
    note_date: n.note_date,
    body: n.body,
    category_id: typeof n.category_id === 'string' && n.category_id ? n.category_id : null,
    persisted: Boolean(n.persisted),
  };
}

export function loadDailyRecordNotesWeekRowsCache(
  userId: string,
  mondayIso: string
): DailyWeekNoteDraft[] | null {
  if (!userId || !mondayIso) return null;
  try {
    const raw = deviceCacheStorage.getItem(weekRowsCacheKey(userId, mondayIso));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed) || !parsed.every(isWeekNoteDraft)) return null;
    return parsed.map(mapWeekDraft);
  } catch {
    return null;
  }
}

export function saveDailyRecordNotesWeekRowsCache(
  userId: string,
  mondayIso: string,
  notes: DailyWeekNoteDraft[]
): void {
  if (!userId || !mondayIso) return;
  try {
    deviceCacheStorage.setItem(weekRowsCacheKey(userId, mondayIso), JSON.stringify(notes.map(mapWeekDraft)));
  } catch {
    /* ignore quota */
  }
}

export function loadDailyRecordNotesWeekRowsDraft(
  userId: string,
  mondayIso: string
): DailyWeekNoteDraft[] | null {
  if (!userId || !mondayIso) return null;
  try {
    const raw = deviceCacheStorage.getItem(weekRowsDraftKey(userId, mondayIso));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed) || !parsed.every(isWeekNoteDraft)) return null;
    return parsed.map(mapWeekDraft);
  } catch {
    return null;
  }
}

export function saveDailyRecordNotesWeekRowsDraft(
  userId: string,
  mondayIso: string,
  notes: DailyWeekNoteDraft[]
): void {
  if (!userId || !mondayIso) return;
  try {
    deviceCacheStorage.setItem(weekRowsDraftKey(userId, mondayIso), JSON.stringify(notes.map(mapWeekDraft)));
  } catch {
    /* ignore quota */
  }
}

export function clearDailyRecordNotesWeekRowsDraft(userId: string, mondayIso: string): void {
  if (!userId || !mondayIso) return;
  try {
    deviceCacheStorage.removeItem(weekRowsDraftKey(userId, mondayIso));
  } catch {
    /* ignore */
  }
}

export function overlayDailyRecordNotesWeekRowsDraft(
  userId: string,
  mondayIso: string,
  saved: DailyWeekNoteDraft[]
): DailyWeekNoteDraft[] {
  const draft = loadDailyRecordNotesWeekRowsDraft(userId, mondayIso);
  if (!draft) return saved;
  const savedById = new Map(saved.map((n) => [n.id, n]));
  const out = new Map<string, DailyWeekNoteDraft>();
  for (const n of saved) out.set(n.id, n);
  for (const n of draft) {
    const base = savedById.get(n.id);
    if (!base || weekNotesSignature([n]) !== weekNotesSignature([base])) out.set(n.id, n);
  }
  return Array.from(out.values());
}

export function persistDailyRecordNotesWeekRowsDraft(
  userId: string,
  mondayIso: string,
  local: DailyWeekNoteDraft[],
  baseline: DailyWeekNoteDraft[]
): void {
  if (weekNotesSignature(local) === weekNotesSignature(baseline)) {
    clearDailyRecordNotesWeekRowsDraft(userId, mondayIso);
    return;
  }
  saveDailyRecordNotesWeekRowsDraft(userId, mondayIso, local);
}

function weekCacheKey(userId: string, mondayIso: string): string {
  return `${WEEK_PREFIX}.${userId}.${mondayIso}`;
}

function weekDraftKey(userId: string, mondayIso: string): string {
  return `${DRAFT_PREFIX}.${userId}.${mondayIso}`;
}

function categoryCacheSeg(categoryId?: string | null): string {
  const id = (categoryId ?? '').trim();
  return id || 'all';
}

function listHeadKey(userId: string, categoryId?: string | null): string {
  return `${LIST_PREFIX}.head.${userId}.${categoryCacheSeg(categoryId)}`;
}

function listPageKey(userId: string, page: number, categoryId?: string | null): string {
  return `${LIST_PREFIX}.page.${userId}.${categoryCacheSeg(categoryId)}.${page}`;
}

export function buildDailyNotesListVersion(totalCount: number, maxUpdatedAt: string): string {
  return `${totalCount}:${maxUpdatedAt.trim()}`;
}

export type DailyNotesListHeadCache = {
  version: string;
  totalCount: number;
};

export type DailyNotesListPageCache = {
  version: string;
  page: number;
  totalCount: number;
  notes: DailyRecordNoteRow[];
};

function isNoteRow(v: unknown): v is DailyRecordNoteRow {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  return (
    typeof r.id === 'string' &&
    typeof r.user_id === 'string' &&
    typeof r.note_date === 'string' &&
    typeof r.body === 'string' &&
    (r.category_id === null || typeof r.category_id === 'string')
  );
}

export function loadDailyRecordNotesWeekCache(
  userId: string,
  mondayIso: string
): DailyRecordNotesByDate | null {
  if (!userId || !mondayIso) return null;
  try {
    const raw = deviceCacheStorage.getItem(weekCacheKey(userId, mondayIso));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const out: DailyRecordNotesByDate = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'string') out[k] = v;
    }
    return out;
  } catch {
    return null;
  }
}

export function saveDailyRecordNotesWeekCache(
  userId: string,
  mondayIso: string,
  byDate: DailyRecordNotesByDate
): void {
  if (!userId || !mondayIso) return;
  try {
    deviceCacheStorage.setItem(weekCacheKey(userId, mondayIso), JSON.stringify(byDate));
  } catch {
    /* ignore quota */
  }
}

/** 서버/캐시 본문 위에, 아직 저장하지 않은 로컬 입력을 유지한다. */
export function mergeDailyRecordNotesKeepingLocalDirty(
  incoming: DailyRecordNotesByDate,
  local: DailyRecordNotesByDate,
  baseline: DailyRecordNotesByDate
): DailyRecordNotesByDate {
  const next: DailyRecordNotesByDate = { ...incoming };
  const keys = new Set([
    ...Object.keys(incoming),
    ...Object.keys(local),
    ...Object.keys(baseline),
  ]);
  keys.forEach((iso) => {
    if ((local[iso] ?? '') !== (baseline[iso] ?? '')) {
      next[iso] = local[iso] ?? '';
    }
  });
  return next;
}

export function loadDailyRecordNotesWeekDraft(
  userId: string,
  mondayIso: string
): DailyRecordNotesByDate | null {
  if (!userId || !mondayIso) return null;
  try {
    const raw = deviceCacheStorage.getItem(weekDraftKey(userId, mondayIso));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const out: DailyRecordNotesByDate = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'string') out[k] = v;
    }
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}

export function saveDailyRecordNotesWeekDraft(
  userId: string,
  mondayIso: string,
  byDate: DailyRecordNotesByDate
): void {
  if (!userId || !mondayIso) return;
  try {
    deviceCacheStorage.setItem(weekDraftKey(userId, mondayIso), JSON.stringify(byDate));
  } catch {
    /* ignore quota */
  }
}

export function clearDailyRecordNotesWeekDraft(userId: string, mondayIso: string): void {
  if (!userId || !mondayIso) return;
  try {
    deviceCacheStorage.removeItem(weekDraftKey(userId, mondayIso));
  } catch {
    /* ignore */
  }
}

/** 저장된 주간 본문 위에 미저장 draft만 덮는다. */
export function overlayDailyRecordNotesWeekDraft(
  userId: string,
  mondayIso: string,
  saved: DailyRecordNotesByDate
): DailyRecordNotesByDate {
  const draft = loadDailyRecordNotesWeekDraft(userId, mondayIso);
  if (!draft) return saved;
  return { ...saved, ...draft };
}

/** 로컬이 baseline과 다르면 dirty 칸만 draft로 두고, 같으면 draft를 지운다. */
export function persistDailyRecordNotesWeekDraft(
  userId: string,
  mondayIso: string,
  local: DailyRecordNotesByDate,
  baseline: DailyRecordNotesByDate
): void {
  const dirty: DailyRecordNotesByDate = {};
  const keys = new Set([...Object.keys(local), ...Object.keys(baseline)]);
  keys.forEach((iso) => {
    if ((local[iso] ?? '') !== (baseline[iso] ?? '')) {
      dirty[iso] = local[iso] ?? '';
    }
  });
  if (Object.keys(dirty).length === 0) {
    clearDailyRecordNotesWeekDraft(userId, mondayIso);
    return;
  }
  saveDailyRecordNotesWeekDraft(userId, mondayIso, dirty);
}

export function loadDailyNotesListHeadCache(
  userId: string,
  categoryId?: string | null
): DailyNotesListHeadCache | null {
  if (!userId) return null;
  try {
    const raw = deviceCacheStorage.getItem(listHeadKey(userId, categoryId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DailyNotesListHeadCache>;
    const version = typeof parsed.version === 'string' ? parsed.version : '';
    const totalCount = typeof parsed.totalCount === 'number' ? parsed.totalCount : NaN;
    if (!version || !Number.isFinite(totalCount) || totalCount < 0) return null;
    return { version, totalCount };
  } catch {
    return null;
  }
}

export function saveDailyNotesListHeadCache(
  userId: string,
  head: DailyNotesListHeadCache,
  categoryId?: string | null
): void {
  if (!userId || !head.version) return;
  try {
    deviceCacheStorage.setItem(listHeadKey(userId, categoryId), JSON.stringify(head));
  } catch {
    /* ignore quota */
  }
}

export function loadDailyNotesListPageCache(
  userId: string,
  page: number,
  categoryId?: string | null
): DailyNotesListPageCache | null {
  if (!userId || page < 1) return null;
  try {
    const raw = deviceCacheStorage.getItem(listPageKey(userId, page, categoryId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DailyNotesListPageCache>;
    const version = typeof parsed.version === 'string' ? parsed.version : '';
    const pageNum = typeof parsed.page === 'number' ? parsed.page : NaN;
    const totalCount = typeof parsed.totalCount === 'number' ? parsed.totalCount : NaN;
    if (!version || pageNum !== page || !Number.isFinite(totalCount) || totalCount < 0) return null;
    if (!Array.isArray(parsed.notes) || !parsed.notes.every(isNoteRow)) return null;
    return {
      version,
      page: pageNum,
      totalCount,
      notes: parsed.notes.map((n) => ({
        id: n.id,
        user_id: n.user_id,
        note_date: n.note_date,
        body: n.body,
        category_id: typeof n.category_id === 'string' ? n.category_id : null,
        created_at: typeof n.created_at === 'string' ? n.created_at : '',
        updated_at: typeof n.updated_at === 'string' ? n.updated_at : '',
      })),
    };
  } catch {
    return null;
  }
}

export function saveDailyNotesListPageCache(
  userId: string,
  payload: DailyNotesListPageCache,
  categoryId?: string | null
): void {
  if (!userId || payload.page < 1 || !payload.version) return;
  try {
    deviceCacheStorage.setItem(listPageKey(userId, payload.page, categoryId), JSON.stringify(payload));
  } catch {
    /* ignore quota */
  }
}

/** 목록 페이지·head 캐시만 제거(주간 캐시는 유지). */
export function clearDailyNotesListCache(userId?: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (userId) {
      const headPrefix = `${LIST_PREFIX}.head.${userId}.`;
      const pagePrefix = `${LIST_PREFIX}.page.${userId}.`;
      const keys: string[] = [];
      for (let i = 0; i < deviceCacheStorage.length; i += 1) {
        const k = deviceCacheStorage.key(i);
        if (!k) continue;
        if (k.startsWith(headPrefix) || k.startsWith(pagePrefix)) keys.push(k);
      }
      for (const k of keys) deviceCacheStorage.removeItem(k);
      return;
    }
    const keys: string[] = [];
    for (let i = 0; i < deviceCacheStorage.length; i += 1) {
      const k = deviceCacheStorage.key(i);
      if (k && k.startsWith(LIST_PREFIX)) keys.push(k);
    }
    for (const k of keys) deviceCacheStorage.removeItem(k);
  } catch {
    /* noop */
  }
}

/** 로그아웃·유저 전환 시 주간+목록 캐시 제거. */
export function clearDailyRecordNotesCaches(userId?: string): void {
  if (typeof window === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < deviceCacheStorage.length; i += 1) {
      const k = deviceCacheStorage.key(i);
      if (!k) continue;
      if (userId) {
        if (
          k.startsWith(`${WEEK_PREFIX}.${userId}.`) ||
          k.startsWith(`${DRAFT_PREFIX}.${userId}.`) ||
          k.startsWith(`${WEEK_ROWS_PREFIX}.${userId}.`) ||
          k.startsWith(`${DRAFT_ROWS_PREFIX}.${userId}.`) ||
          k.startsWith(`${LIST_PREFIX}.head.${userId}.`) ||
          k.startsWith(`${LIST_PREFIX}.page.${userId}.`)
        ) {
          keys.push(k);
        }
      } else if (
        k.startsWith(WEEK_PREFIX) ||
        k.startsWith(DRAFT_PREFIX) ||
        k.startsWith(WEEK_ROWS_PREFIX) ||
        k.startsWith(DRAFT_ROWS_PREFIX) ||
        k.startsWith(LIST_PREFIX)
      ) {
        keys.push(k);
      }
    }
    for (const k of keys) deviceCacheStorage.removeItem(k);
    clearDailyNoteCategoriesCache(userId);
  } catch {
    /* noop */
  }
}
